import { AxiosPromise } from 'axios';

import { mmHttp, MmRequestConfig } from './mmHttp';
import { readPage, serializeFiltersToQuery } from './pagination';
import { isCanceled } from '../core/errors';
import {
  MmLevelsResponse,
  MmPage,
  MmProductLevelsRequest,
  MmStoreLevelsRequest,
  MmTaskFilters,
  MmTaskFiltersQuery,
  MmTasksInfoResponse,
} from '../types/dto';
import { MmProductRow, MmSelectOption } from '../types/domain';

const DICTIONARIES_PATH = '/dictionaries';
const CATALOG_PATH = '/catalog';

export function fetchStoreLevels(
  payload: MmStoreLevelsRequest,
  config?: MmRequestConfig,
): AxiosPromise<MmLevelsResponse> {
  return mmHttp.post(
    DICTIONARIES_PATH + '/store-levels',
    payload,
    Object.assign({ mmSection: 'dictionaries/store-levels' }, config),
  );
}

export function fetchProductLevels(
  payload: MmProductLevelsRequest,
  config?: MmRequestConfig,
): AxiosPromise<MmLevelsResponse> {
  return mmHttp.post(
    DICTIONARIES_PATH + '/product-levels',
    payload,
    Object.assign({ mmSection: 'dictionaries/product-levels' }, config),
  );
}

/** Отдельной ручки нет: признак наличия подкатегорий выводим из справочника уровней */
export function fetchTasksInfo(
  tradingNetworkId: number,
  config?: MmRequestConfig,
): AxiosPromise<MmTasksInfoResponse> {
  return mmHttp.get(
    '/tasks/info',
    Object.assign({ params: { tradingNetworkId: tradingNetworkId }, mmSection: 'tasks/info' }, config),
  );
}

/** Справочник подписей, а не перечень допустимых кодов: неизвестный код показываем как есть */
export interface MmSkipReason {
  code: string;
  name: string;
  description: string | null;
}

/** Операции на сервере пока нет: при отказе коды остаются сырыми */
export function fetchSkipReasons(
  tradingNetworkId: number,
  config?: MmRequestConfig,
): AxiosPromise<{ items: MmSkipReason[] }> {
  return mmHttp.get(
    DICTIONARIES_PATH + '/skip-reasons',
    Object.assign(
      { params: { tradingNetworkId: tradingNetworkId }, mmSection: 'dictionaries/skip-reasons' },
      config,
    ),
  );
}

/** POST с телом: массивы в query axios сериализует как key[]; taskType кладём в тело только явно */
export function fetchProducts(
  tradingNetworkId: number,
  filters: MmTaskFilters,
  pageParams: { [key: string]: any },
  config?: MmRequestConfig,
  taskType?: string | null,
): Promise<MmPage<MmProductRow>> {
  const payload: { tradingNetworkId: number; filters: MmTaskFiltersQuery; taskType?: string } = {
    tradingNetworkId: tradingNetworkId,
    filters: serializeFiltersToQuery(filters),
  };

  if (taskType !== undefined && taskType !== null && taskType !== '') {
    payload.taskType = taskType;
  }

  return mmHttp
    .post(
      CATALOG_PATH + '/products',
      payload,
      Object.assign({ params: pageParams, mmSection: 'catalog/products' }, config),
    )
    .then((response) => readPage<MmProductRow>(response.data));
}

export interface MmGroupExample {
  category: MmSelectOption;
  subcategories: MmSelectOption[];
}

export function fetchGroupExamples(
  tradingNetworkId: number,
  groupIds: number[],
  config?: MmRequestConfig,
): Promise<MmGroupExample[]> {
  const categoriesRequest: MmProductLevelsRequest = {
    tradingNetworkId: tradingNetworkId,
    type: 'category',
    groups: groupIds.slice(),
    categories: [],
  };

  return fetchProductLevels(categoriesRequest, config).then((categoriesResponse) => {
    const categories = categoriesResponse.data && categoriesResponse.data.items ? categoriesResponse.data.items : [];

    if (categories.length === 0) {
      return [];
    }

    /** Запрашиваем по одной категории: в плоском ответе нет родителя, группировать подкатегории нечем */
    const requests = categories.map((category) => {
      const subcategoriesRequest: MmProductLevelsRequest = {
        tradingNetworkId: tradingNetworkId,
        type: 'subcategory',
        groups: groupIds.slice(),
        categories: [category.value],
      };

      return fetchProductLevels(subcategoriesRequest, config)
        .then((response) => {
          const items = response.data && response.data.items ? response.data.items : [];

          return { category: category, subcategories: items };
        })
        .catch((error) => {
          if (isCanceled(error)) {
            throw error;
          }

          return { category: category, subcategories: [] };
        });
    });

    return Promise.all(requests);
  });
}
